import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n";
import { useDiscovery } from "@/components/Layout";
import { MaskedLines, Reveal, FadeIn, ParallaxImage, Kicker } from "@/components/motion";

const STRIP = [
  { src: "/womens-health.jpg", key: 3 },
  { src: "https://images.pexels.com/photos/8001036/pexels-photo-8001036.jpeg?auto=compress&cs=tinysrgb&w=1260", key: 5 },
  { src: "/leadership.jpg", key: 6 },
];

export default function Expertise() {
  const { t } = useLang();
  const open = useDiscovery();
  const e = t.expertise;

  return (
    <>
      <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-20 grain" data-testid="expertise-hero">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-sage-100/70 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn delay={0.1}>
            <Kicker>{e.kicker}</Kicker>
          </FadeIn>
          <MaskedLines
            className="mt-9 max-w-3xl font-serif text-4xl leading-[1.1] tracking-tight text-navy-900 sm:text-5xl lg:text-6xl"
            delay={0.25}
            lines={[e.title]}
          />
          <Reveal delay={0.6}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-navy-900/70 sm:text-lg" data-testid="expertise-intro">
              {e.intro}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-16 sm:pb-20" data-testid="expertise-strip">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 sm:grid-cols-3 sm:px-8">
          {STRIP.map((s, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <ParallaxImage
                src={s.src}
                alt={e.items[s.key].title}
                testId={`expertise-strip-image-${i}`}
                className="aspect-[4/3] w-full"
                speed={0.08}
              />
              <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-navy-900/50" data-testid={`expertise-strip-caption-${i}`}>
                {e.items[s.key].title}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="pb-20 sm:pb-28" data-testid="expertise-list">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          {e.items.map((item, i) => (
            <Reveal key={i} className="group grid gap-4 border-t border-navy-900/10 py-9 transition-colors duration-500 hover:bg-white sm:grid-cols-12 sm:items-baseline sm:px-4">
              <div className="sm:col-span-1">
                <span className="font-mono text-sm text-gold-deep" data-testid={`expertise-num-${i}`}>{item.n}</span>
              </div>
              <div className="sm:col-span-4">
                <h2 className="font-serif text-xl tracking-tight text-navy-900 transition-colors duration-500 group-hover:text-sage-700 sm:text-2xl" data-testid={`expertise-title-${i}`}>
                  {item.title}
                </h2>
              </div>
              <div className="sm:col-span-7">
                <p className="max-w-xl text-sm leading-relaxed text-navy-900/65 sm:text-base">{item.text}</p>
              </div>
            </Reveal>
          ))}
          <Reveal className="border-t border-navy-900/10 pt-14 text-center">
            <button
              onClick={open}
              data-testid="expertise-cta-btn"
              className="group inline-flex items-center gap-2.5 bg-navy-900 px-8 py-4 text-sm font-medium tracking-wide text-cream transition-colors duration-300 hover:bg-sage-700"
            >
              {e.cta}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </Reveal>
        </div>
      </section>
    </>
  );
}
